import type { OwnedMonsterView, TeamAction } from '../../shared/protocol.ts';
import { BOX_CAPACITY, BOX_PAGE_SIZE, BOX_PAGES, BOX_SORTS, TEAM_SIZE, type BoxSort } from '../../shared/game-rules.ts';
import { confirmDialog } from '../common/confirm-dialog.ts';
import { $, $$, el } from '../common/dom.ts';
import { icon } from '../common/icons.ts';
import { Modal } from '../common/modal.ts';
import { reorderActions } from './team-panel.ts';
import type { CardAction, OpenMonster } from './monster-card.ts';
import { creatureText } from '../../shared/game-config.ts';

type Place = { team: number } | { box: number };

const SORT_STORAGE_KEY = 'pocket_box_sort';
/** Appui long (au doigt) avant de soulever un monstre : un glissement plus court fait défiler. */
const TOUCH_HOLD_MS = 280;
/** Distance (px) à partir de laquelle un appui devient un glisser à la souris, ou annule l'appui long au doigt. */
const DRAG_THRESHOLD = 8;
/** Temps passé sur une flèche de page, en glissant un monstre, avant de changer de page. */
const PAGE_FLIP_MS = 450;

function readSort(): { key: BoxSort; descending: boolean } {
  try {
    const saved = JSON.parse(localStorage.getItem(SORT_STORAGE_KEY) ?? 'null') as { key?: unknown; descending?: unknown } | null;
    if (saved && typeof saved.key === 'string' && saved.key in BOX_SORTS) return { key: saved.key as BoxSort, descending: saved.descending !== false };
  } catch { /* stockage indisponible : ordre par défaut */ }
  return { key: 'level', descending: true };
}

const placeKey = (place: Place) => ('team' in place ? `team:${place.team}` : `box:${place.box}`);

function parsePlace(key: string | undefined): Place | null {
  const [kind, value] = (key ?? '').split(':');
  const index = Number(value);
  if (!Number.isInteger(index)) return null;
  return kind === 'team' ? { team: index } : kind === 'box' ? { box: index } : null;
}

interface Drag {
  pointerId: number;
  id: number;
  from: Place;
  startX: number;
  startY: number;
  /** Le monstre est soulevé (sinon : appui en attente de glisser ou d'appui long). */
  active: boolean;
  holdTimer: number | null;
  ghost: HTMLElement | null;
  flip: { step: number; timer: number } | null;
}

/**
 * PC de stockage : l'équipe et une boîte en pages de cases. Les monstres se glissent d'une case à l'autre
 * (échange si la case est prise), entre l'équipe et la boîte ; « Ranger » trie toute la boîte.
 */
export class StoragePanel extends Modal {
  private readonly teamGrid = $('.storage-team', this.root);
  private readonly boxGrid = $('.storage-box', this.root);
  private readonly sortKey = $<HTMLSelectElement>('.storage-sort-key', this.root);
  private readonly sortDirection = $<HTMLButtonElement>('.storage-sort-dir', this.root);
  private readonly onAction: (action: TeamAction) => void;
  private readonly onOpen: OpenMonster;
  private collection: OwnedMonsterView[] = [];
  private page = 0;
  private sort = readSort();
  private drag: Drag | null = null;
  /** Un glisser vient de finir : le clic qui suit n'ouvre pas la fiche. */
  private suppressClick = false;

  constructor(onAction: (action: TeamAction) => void, onOpen: OpenMonster) {
    super('#storage');
    this.onAction = onAction;
    this.onOpen = onOpen;
    this.sortKey.replaceChildren(...Object.entries(BOX_SORTS).map(([value, label]) => el('option', { value, textContent: label })));
    this.sortKey.value = this.sort.key;
    this.sortKey.addEventListener('change', () => this.setSort({ ...this.sort, key: this.sortKey.value as BoxSort }));
    this.sortDirection.addEventListener('click', () => this.setSort({ ...this.sort, descending: !this.sort.descending }));
    $('.storage-sort-apply', this.root).addEventListener('click', () => this.onAction({ action: 'sort', ...this.sort }));
    for (const button of $$<HTMLButtonElement>('[data-page-step]', this.root)) {
      button.addEventListener('click', () => this.turnPage(Number(button.dataset.pageStep)));
    }
    this.renderSortDirection();
    this.bindDrag();
  }

  override open(): void {
    super.open();
    this.render(this.collection);
  }

  override close(): void {
    this.endDrag();
    super.close();
  }

  render(collection: OwnedMonsterView[]): void {
    this.collection = collection;
    if (!this.isOpen) return;
    const team = this.team();
    const boxed = collection.filter((monster) => monster.boxSlot !== null);
    const byCell = new Map(boxed.map((monster) => [monster.boxSlot, monster]));
    const first = this.page * BOX_PAGE_SIZE;

    $('.storage-team-count', this.root).textContent = `${team.length}/${TEAM_SIZE}`;
    $('.storage-box-count', this.root).textContent = `${boxed.length} / ${BOX_CAPACITY}`;
    $('.storage-page-label', this.root).textContent = `Boîte ${this.page + 1} / ${BOX_PAGES}`;
    this.teamGrid.replaceChildren(...Array.from({ length: TEAM_SIZE }, (_, index) => this.slot({ team: index }, team[index])));
    this.boxGrid.replaceChildren(...Array.from({ length: BOX_PAGE_SIZE }, (_, offset) => this.slot({ box: first + offset }, byCell.get(first + offset))));
    $('.storage-hint', this.root).textContent = boxed.length >= BOX_CAPACITY
      ? creatureText('Boîte pleine : relâchez des {monstres} pour pouvoir en capturer d\'autres.')
      : creatureText('Glissez {un monstre} sur une case pour {le} déplacer (au doigt : appui long). Le premier de l\'équipe part au combat.');
  }

  private team(): OwnedMonsterView[] {
    return this.collection.filter((monster) => monster.slot !== null).sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0));
  }

  /** Monstres de la boîte, case par case (toutes pages). */
  private boxOrder(): OwnedMonsterView[] {
    return this.collection.filter((monster) => monster.boxSlot !== null).sort((a, b) => (a.boxSlot ?? 0) - (b.boxSlot ?? 0));
  }

  /** Une case : vide, ou avec l'image du monstre (appui : sa fiche ; glisser : le déplacer). */
  private slot(place: Place, monster: OwnedMonsterView | undefined): HTMLElement {
    const cell = el('div', { className: 'slot' });
    cell.dataset.drop = placeKey(place);
    if (!monster) return cell;
    const button = el('button', { className: `slot-monster ${monster.shiny ? 'is-shiny' : ''}`, title: `${monster.name} · Nv ${monster.level}` },
      el('img', { src: monster.image, alt: monster.name, draggable: false, className: monster.shiny ? 'shiny' : '' }),
      el('span', { className: 'slot-level', textContent: String(monster.level) }),
      ...(monster.shiny ? [icon('sparkles', { label: 'Chromatique', className: 'slot-shiny' })] : []));
    button.dataset.monster = String(monster.id);
    button.addEventListener('click', () => {
      if (this.suppressClick) return;
      this.openSheet(monster.id);
    });
    cell.append(button);
    return cell;
  }

  private turnPage(step: number): void {
    this.page = (this.page + step + BOX_PAGES) % BOX_PAGES;
    this.render(this.collection);
  }

  private setSort(sort: { key: BoxSort; descending: boolean }): void {
    this.sort = sort;
    try {
      localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sort));
    } catch { /* stockage indisponible : l'ordre vaut pour cette session */ }
    this.renderSortDirection();
  }

  private renderSortDirection(): void {
    const label = this.sort.descending ? 'Décroissant' : 'Croissant';
    this.sortDirection.replaceChildren(icon(this.sort.descending ? 'arrow-down' : 'arrow-up', { label }));
    this.sortDirection.title = `${label} — inverser l'ordre`;
  }

  // ---------- Fiche ----------

  /** Ouvre la fiche d'un monstre, avec des flèches vers ses voisins (l'équipe, ou la boîte case par case, qui boucle). */
  private openSheet(id: number): void {
    const monster = this.collection.find((owned) => owned.id === id);
    if (!monster) return;
    const list = monster.slot !== null ? this.team() : this.boxOrder();
    const index = list.findIndex((owned) => owned.id === id);
    // La boîte suit la fiche : on voit la page du monstre affiché.
    if (monster.boxSlot !== null && Math.floor(monster.boxSlot / BOX_PAGE_SIZE) !== this.page) {
      this.page = Math.floor(monster.boxSlot / BOX_PAGE_SIZE);
      this.render(this.collection);
    }
    const neighbour = (step: number) => () => {
      const next = list[(index + step + list.length) % list.length];
      if (next) this.openSheet(next.id);
    };
    this.onOpen(monster, this.actionsFor(monster), list.length > 1
      ? { previous: neighbour(-1), next: neighbour(1), position: `${index + 1} / ${list.length}` }
      : undefined);
  }

  private actionsFor(monster: OwnedMonsterView): CardAction[] {
    const team = this.team();
    const release: CardAction = {
      label: 'Relâcher',
      variant: 'danger',
      onClick: async () => {
        const confirmed = await confirmDialog({ title: `Relâcher ${monster.name} ?`, message: 'Il retournera à la vie sauvage.', icon: 'flee', confirmLabel: 'Relâcher', danger: true });
        if (confirmed) this.onAction({ action: 'release', id: monster.id });
      },
    };
    if (monster.slot !== null) {
      return [
        ...reorderActions(monster.slot, team.length, (to) => this.onAction({ action: 'move', id: monster.id, to })),
        ...(team.length > 1 ? [{ label: 'Boîte', icon: 'box' as const, title: 'Ranger dans la boîte', onClick: () => this.onAction({ action: 'toggle', id: monster.id }), variant: 'secondary' as const }] : []),
        ...(team.length > 1 ? [release] : []),
      ];
    }
    return [
      ...(team.length >= TEAM_SIZE ? [] : [{ label: 'Équipe', icon: 'team' as const, title: 'Ajouter à l\'équipe', onClick: () => this.onAction({ action: 'toggle', id: monster.id }) }]),
      release,
    ];
  }

  // ---------- Glisser-déposer (souris et doigt, via les événements de pointeur) ----------

  private bindDrag(): void {
    this.root.addEventListener('pointerdown', (event) => this.onPointerDown(event));
    window.addEventListener('pointermove', (event) => this.onPointerMove(event));
    window.addEventListener('pointerup', (event) => this.onPointerUp(event));
    window.addEventListener('pointercancel', (event) => { if (event.pointerId === this.drag?.pointerId) this.endDrag(); });
    // Monstre soulevé au doigt : la page ne défile pas pendant qu'on le déplace.
    window.addEventListener('touchmove', (event) => { if (this.drag?.active) event.preventDefault(); }, { passive: false });
    this.root.addEventListener('contextmenu', (event) => { if ((event.target as Element).closest('.slot-monster')) event.preventDefault(); });
  }

  private onPointerDown(event: PointerEvent): void {
    if (this.drag || event.button !== 0) return;
    const button = (event.target as Element).closest<HTMLElement>('[data-monster]');
    const from = parsePlace(button?.closest<HTMLElement>('[data-drop]')?.dataset.drop);
    if (!button || !from) return;
    this.suppressClick = false;
    this.drag = {
      pointerId: event.pointerId, id: Number(button.dataset.monster), from, startX: event.clientX, startY: event.clientY,
      active: false, holdTimer: null, ghost: null, flip: null,
    };
    if (event.pointerType === 'touch') {
      const drag = this.drag;
      drag.holdTimer = window.setTimeout(() => this.lift(drag, drag.startX, drag.startY), TOUCH_HOLD_MS);
    }
  }

  private onPointerMove(event: PointerEvent): void {
    const drag = this.drag;
    if (drag?.pointerId !== event.pointerId) return;
    if (!drag.active) {
      if (Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < DRAG_THRESHOLD) return;
      // Au doigt, un glissement avant l'appui long fait défiler : pas de glisser-déposer.
      if (event.pointerType === 'touch') return this.endDrag();
      this.lift(drag, event.clientX, event.clientY);
    }
    event.preventDefault();
    this.moveGhost(drag, event.clientX, event.clientY);
    const under = this.elementAt(event.clientX, event.clientY);
    for (const cell of $$('.slot.drop-target', this.root)) cell.classList.remove('drop-target');
    under?.closest('.slot')?.classList.add('drop-target');
    this.hoverPageButton(drag, under?.closest<HTMLElement>('[data-page-step]') ?? null);
  }

  private onPointerUp(event: PointerEvent): void {
    const drag = this.drag;
    if (drag?.pointerId !== event.pointerId) return;
    if (drag.active) {
      const to = parsePlace(this.elementAt(event.clientX, event.clientY)?.closest<HTMLElement>('[data-drop]')?.dataset.drop);
      if (to && placeKey(to) !== placeKey(drag.from)) this.onAction({ action: 'place', id: drag.id, to });
      // Le clic qui suit le relâchement ne doit pas ouvrir la fiche.
      this.suppressClick = true;
      window.setTimeout(() => { this.suppressClick = false; }, 0);
    }
    this.endDrag();
  }

  /** Soulève le monstre : une copie de son image suit le pointeur. */
  private lift(drag: Drag, x: number, y: number): void {
    if (this.drag !== drag || drag.active) return;
    drag.active = true;
    const source = $<HTMLElement>(`[data-monster="${drag.id}"]`, this.root);
    source.classList.add('lifted');
    const image = source.querySelector('img');
    drag.ghost = el('div', { className: 'slot-ghost' }, ...(image ? [image.cloneNode() as HTMLImageElement] : []));
    document.body.append(drag.ghost);
    this.moveGhost(drag, x, y);
    navigator.vibrate?.(15);
  }

  private moveGhost(drag: Drag, x: number, y: number): void {
    if (drag.ghost) drag.ghost.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
  }

  /** Élément sous le pointeur (la copie qui le suit ne capte pas les pointeurs). */
  private elementAt(x: number, y: number): Element | null {
    const element = document.elementFromPoint(x, y);
    return element && this.root.contains(element) ? element : null;
  }

  /** En glissant un monstre, rester sur une flèche de page change de page. */
  private hoverPageButton(drag: Drag, button: HTMLElement | null): void {
    const step = button ? Number(button.dataset.pageStep) : 0;
    if (drag.flip?.step === step) return;
    if (drag.flip) window.clearTimeout(drag.flip.timer);
    drag.flip = step ? { step, timer: window.setTimeout(() => { drag.flip = null; this.turnPage(step); this.markLifted(drag); }, PAGE_FLIP_MS) } : null;
  }

  /** Après un changement de page, le monstre soulevé reste marqué s'il est visible. */
  private markLifted(drag: Drag): void {
    this.root.querySelector(`[data-monster="${drag.id}"]`)?.classList.add('lifted');
  }

  private endDrag(): void {
    const drag = this.drag;
    if (!drag) return;
    if (drag.holdTimer !== null) window.clearTimeout(drag.holdTimer);
    if (drag.flip) window.clearTimeout(drag.flip.timer);
    drag.ghost?.remove();
    for (const element of $$('.lifted, .drop-target', this.root)) element.classList.remove('lifted', 'drop-target');
    this.drag = null;
  }
}
